import { useEffect, useEffectEvent } from 'react';

/**
 * Shared "you've been away" catch-up: fires `onStale` when `dataUpdatedAt` is
 * at least `staleMs` old AND the tab is visible. Two triggers cover the three
 * ways a tab can come to hold stale data:
 *   (a) mount / navigate-back with already-stale data,
 *   (b) a backgrounded tab regaining focus (`visibilitychange`),
 *   (c) a tab that's stayed open and visible the whole time — `visibilitychange`
 *       never fires for that case, so a timer is armed for the exact moment
 *       the data turns stale.
 *
 * Extracted from PortfolioPage's inline `syncIfStale` (its only consumer
 * today — the Activity Alerts card was going to share it but pivoted to a
 * reader-controlled "changes available" probe instead).
 *
 * `onStale` is read via `useEffectEvent` so it always sees the latest closure
 * (latest `refetch`/`runVerify`, latest state) without the two subscriptions
 * below re-running on every render.
 */
export function useStaleSync(dataUpdatedAt: number, staleMs: number, onStale: () => void): void {
  const checkStale = useEffectEvent(() => {
    if (document.visibilityState !== 'visible') return;
    if (dataUpdatedAt > 0 && Date.now() - dataUpdatedAt >= staleMs) onStale();
  });

  // (b) re-focus a backgrounded/hidden tab → subscribe once for the lifetime.
  useEffect(() => {
    const onVisible = () => checkStale();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // (a) mount / navigate-back, and (c) a tab left open & visible → a timer
  // armed for exactly when the data turns stale. On mount with already-stale
  // data the delay clamps to ~0 so it syncs right away; re-arms whenever
  // `dataUpdatedAt` changes (a fresh sync pushes the due time out again).
  // The extra +500ms is slack against clock/scheduling jitter so the timer
  // never fires a hair before the data is actually `staleMs` old.
  useEffect(() => {
    if (dataUpdatedAt <= 0) return;
    const dueInMs = Math.max(0, staleMs - (Date.now() - dataUpdatedAt)) + 500;
    const timer = setTimeout(checkStale, dueInMs);
    return () => clearTimeout(timer);
  }, [dataUpdatedAt, staleMs]);
}
